/**
 * Theme preference — light / dark / system.
 *
 * The dark tokens have existed in `index.css` since the first pass, keyed off
 * `[data-theme='dark']`, but nothing in the app ever set that attribute: the
 * console had a complete dark palette that no admin could reach. This store is
 * the switch, and it persists, because an operator who works nights should not
 * re-pick it every morning.
 *
 * 'system' is a real third state, not a synonym for light — it tracks the OS
 * and keeps tracking it, so a machine that flips at sunset flips with it.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeChoice = 'light' | 'dark' | 'system';
/** What is actually painted, once 'system' has been resolved. */
export type ResolvedTheme = 'light' | 'dark';

const MEDIA = '(prefers-color-scheme: dark)';

function system_theme(): ResolvedTheme {
  return window.matchMedia?.(MEDIA).matches ? 'dark' : 'light';
}

export function resolve_theme(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? system_theme() : choice;
}

/**
 * Write the attribute the CSS actually keys off, plus `color-scheme` so the
 * browser's own chrome — scrollbars, native date pickers, form controls, the
 * caret — switches with us. Without it a dark console renders bright white
 * scrollbars and an unreadable date picker.
 */
function apply_theme(choice: ThemeChoice): void {
  const resolved = resolve_theme(choice);
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.style.colorScheme = resolved;
}

type ThemeState = {
  choice: ThemeChoice;
  set_choice: (next: ThemeChoice) => void;
};

export const use_theme_store = create<ThemeState>()(
  persist(
    (set) => ({
      choice: 'system',
      set_choice: (next) => {
        apply_theme(next);
        set({ choice: next });
      },
    }),
    {
      name: 'pm-admin-theme',
      onRehydrateStorage: () => (state) => {
        // Persisted state arrives after first paint, so re-apply once it lands.
        apply_theme(state?.choice ?? 'system');
      },
    },
  ),
);

/**
 * Call once at startup. Applies the stored choice immediately and keeps
 * 'system' honest by following the OS for as long as 'system' is selected.
 */
export function init_theme(): () => void {
  apply_theme(use_theme_store.getState().choice);
  const mq = window.matchMedia?.(MEDIA);
  if (!mq) return () => {};
  const on_change = () => {
    if (use_theme_store.getState().choice === 'system') apply_theme('system');
  };
  mq.addEventListener('change', on_change);
  return () => mq.removeEventListener('change', on_change);
}
